import { useCallback, useEffect, useState } from "react";
import { Button } from "./components/ui/button.tsx";
import { DashboardPage } from "./features/dashboard/DashboardPage.tsx";
import { DiagnosticsPage } from "./features/diagnostics/DiagnosticsPage.tsx";
import { EventsPage } from "./features/events/EventsPage.tsx";
import { GroupsPage } from "./features/groups/GroupsPage.tsx";
import { OffersPage } from "./features/offers/OffersPage.tsx";
import { PublicationsPage } from "./features/publications/PublicationsPage.tsx";
import { RulesPage } from "./features/rules/RulesPage.tsx";
import { SettingsPage } from "./features/settings/SettingsPage.tsx";
import { TemplatesPage } from "./features/templates/TemplatesPage.tsx";
import { WahaPage } from "./features/waha/WahaPage.tsx";
import { LoginPage } from "./features/auth/LoginPage.tsx";
import { clearSession } from "./lib/http.ts";

type Page =
  | "dashboard"
  | "groups"
  | "offers"
  | "publications"
  | "rules"
  | "templates"
  | "events"
  | "waha"
  | "settings"
  | "diagnostics";

const PAGES: Array<[Page, string]> = [
  ["dashboard", "Dashboard"],
  ["groups", "Grupos"],
  ["offers", "Ofertas"],
  ["publications", "Publicações"],
  ["rules", "Afiliados e links"],
  ["templates", "Templates"],
  ["events", "Eventos"],
  ["waha", "Integração WAHA"],
  ["settings", "Configurações"],
  ["diagnostics", "Diagnóstico"],
];

export function App() {
  const [authenticated, setAuthenticated] = useState(() =>
    typeof window === "undefined"
      ? false
      : Boolean(localStorage.getItem("affiliate_flow_auth_token"))
  );
  const [page, setPage] = useState<Page>("dashboard");
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return localStorage.getItem("affiliate_flow_theme") === "dark";
    } catch {
      return document.documentElement.classList.contains("dark");
    }
  });

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    try {
      localStorage.setItem("affiliate_flow_theme", next ? "dark" : "light");
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem("affiliate_flow_theme");
      if (stored === "dark") {
        setDark(true);
        document.documentElement.classList.add("dark");
      }
    } catch {
      // ignore
    }
  }, []);

  const logout = () => {
    clearSession();
    setAuthenticated(false);
  };

  if (!authenticated) {
    return <LoginPage onLogin={() => setAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground md:grid md:grid-cols-[17rem_1fr]">
      <aside className="flex flex-col border-b bg-card/95 md:min-h-screen md:border-b-0 md:border-r">
        <div className="border-b border-border/70 p-5">
          <h1 className="text-xl font-semibold tracking-tight">Affiliate Flow</h1>
          <p className="mt-1 max-w-[18rem] text-xs leading-5 text-muted-foreground">
            Central de captura, revisão e publicação.
          </p>
        </div>
        <nav
          aria-label="Navegação principal"
          className="flex gap-1 overflow-x-auto px-3 py-3 md:block md:space-y-1"
        >
          {PAGES.map(([id, label]) => (
            <button
              key={id}
              type="button"
              aria-current={page === id ? "page" : undefined}
              onClick={() => setPage(id)}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-left text-sm md:block md:w-full ${
                page === id
                  ? "bg-primary font-medium text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="mt-auto border-t border-border/70 p-3 space-y-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="w-full rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
            aria-label={dark ? "Ativar tema claro" : "Ativar tema escuro"}
          >
            {dark ? "☀ Tema claro" : "🌙 Tema escuro"}
          </button>
          <button
            type="button"
            onClick={logout}
            className="w-full rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Sair
          </button>
        </div>
      </aside>

      <main className="mx-auto w-full max-w-7xl p-4 sm:p-6 lg:p-8">
        <header className="mb-7 flex flex-wrap items-end justify-between gap-4 border-b border-border/80 pb-5">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {PAGES.find(([id]) => id === page)?.[1]}
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
              Dados operacionais reais, sem registros demonstrativos.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Atualizar
            </Button>
          </div>
        </header>

        {renderPage(page)}
      </main>
    </div>
  );
}

function renderPage(page: Page): JSX.Element {
  switch (page) {
    case "dashboard":
      return <DashboardPage />;
    case "offers":
      return <OffersPage />;
    case "groups":
      return <GroupsPage />;
    case "publications":
      return <PublicationsPage />;
    case "rules":
      return <RulesPage />;
    case "templates":
      return <TemplatesPage />;
    case "events":
      return <EventsPage />;
    case "waha":
      return <WahaPage />;
    case "settings":
      return <SettingsPage />;
    case "diagnostics":
      return <DiagnosticsPage />;
  }
}
